import { useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import { Box } from 'ui-primitives/src/Box'
import { useBreakpoints } from 'ui-primitives/src/hooks/useBreakpoints'
import { spacing } from 'ui-primitives/src/utils/spacing'
import { Disclaimer } from 'flow-builder/src/components/Disclaimer'
import { Button } from 'flow-builder/src/components/form/Button'
import { Picker } from 'flow-builder/src/components/Picker/Picker'
import { Text } from 'flow-builder/src/components/Text/Text'

import { bookingBarMessages } from './messages'
import type { PopoverState } from './Popover'
import {
  Popover,
  PopoverContent,
  type PopoverContentProps,
  PopoverTrigger,
} from './Popover'
import type { Room } from './urlParams'

export const defaultRoom: Room = { adults: 2, children: 0, infants: 0 }

/**
 * RoomConfigurator component
 *
 * The `RoomConfigurator` component allows users to configure the number of rooms and guests for a hotel booking.
 *
 * @param props - The properties for the `RoomConfigurator` component.
 * @param props.popover - Configuration for the popover.
 * @param props.popover.open - A boolean indicating whether the popover is open.
 * @param props.popover.setOpen - A function to toggle the popover's open state.
 * @param props.popover.onNext - An optional callback function triggered when the "Next" button is clicked.
 * @param props.popover.anchor - An optional HTML element used as the anchor for the popover.
 * @param props.onChange - A callback function triggered when the room configuration changes.
 * @param props.value - An array of `Room` objects representing the current room configuration.
 * @returns The rendered `RoomConfigurator` component.
 */
export const RoomConfigurator = ({
  popover: { open, setOpen, onNext, anchor },
  onChange,
  value,
}: {
  popover: PopoverContentProps & PopoverState
  onChange: (value: Room[]) => void
  value: Room[]
}) => {
  const { formatMessage } = useIntl()
  const { isMobile } = useBreakpoints()
  const numberOfRooms = value.length
  const numberOfAdults = value.reduce(
    (acc, room) => acc + (room.adults ?? 0),
    0
  )

  const anchorRef = useRef<HTMLDivElement | null>(null)
  if (anchor) {
    anchorRef.current = anchor
  }

  const updateNumberOfRooms = (count: number) => {
    if (numberOfRooms === count) {
      return
    }
    if (count < numberOfRooms) {
      onChange(value.slice(0, count))
    } else {
      const newRooms = Array.from(
        { length: count - numberOfRooms },
        () => defaultRoom
      )
      onChange([...value, ...newRooms])
    }
  }

  const updateRoomOccupancy = (roomIndex: number, adults: number) => {
    const updatedRooms = value.map((room, index) =>
      index === roomIndex ? { ...room, adults } : room
    )
    onChange(updatedRooms)
  }

  return (
    <Popover
      modal={isMobile}
      open={open}
      onOpenChange={(open: boolean) => {
        setOpen(open)
      }}
    >
      <PopoverTrigger
        title={formatMessage(bookingBarMessages.menu.guests)}
        value={formatMessage(bookingBarMessages.menu.guestsInRooms, {
          rooms: numberOfRooms,
          guests: numberOfAdults,
        })}
        onClick={() => {
          if (!open) {
            setOpen(true)
          }
        }}
        selected={open}
      />
      <PopoverContent onNext={onNext} anchor={anchorRef.current}>
        <Box
          flexDirection="column"
          justifyContent="center"
          padding={spacing[1]}
        >
          {isMobile && (
            <Box padding={spacing[1]} paddingBottom={spacing[2]}>
              <Text
                type="headingMedium"
                value={formatMessage(bookingBarMessages.title.howManyGuests)}
              />
            </Box>
          )}
          <Box flexDirection="row" padding={1}>
            <Box justifyContent="center" marginRight={spacing[1]}>
              <Text
                type="label"
                value={formatMessage(bookingBarMessages.label.numberOfRooms)}
              />
            </Box>
            <Box flexDirection="row" gap="2px" $width="160px">
              {[1, 2, 3, 4].map(number => (
                <Button
                  key={number}
                  onClick={() => {
                    updateNumberOfRooms(number)
                  }}
                  preset="transparent"
                  weight="light"
                  style={{
                    width: '30px',
                    height: '30px',
                    padding: 0,
                    border: `${numberOfRooms === number ? '2px var(--color-accent)' : '1px var(--border-color)'} solid`,
                    backgroundColor: 'transparent',
                    color: 'var(--color-text)',
                  }}
                >
                  {number}
                </Button>
              ))}
            </Box>
          </Box>
          <Box
            padding={`${spacing[0.5]} ${spacing[1]} ${spacing[1.5]} ${spacing[1]}`}
          >
            <Text
              type="text"
              value={formatMessage(bookingBarMessages.label.moreRoomsEmail)}
            />
          </Box>
          <Box
            style={{
              overflow: 'auto',
              maxHeight: isMobile ? 'auto' : '40vh',
            }}
          >
            {value.map((room, index) => {
              const roomNumber = index + 1
              return (
                <RoomPicker
                  key={roomNumber}
                  id={roomNumber}
                  value={room}
                  onChange={adults => {
                    updateRoomOccupancy(roomNumber - 1, adults)
                  }}
                />
              )
            })}
          </Box>
        </Box>
      </PopoverContent>
    </Popover>
  )
}

const RoomPicker = ({
  id,
  onChange,
  value: { adults = 2 },
}: {
  id: number
  onChange: (value: number) => void
  value: Room
}) => {
  const { formatMessage } = useIntl()
  const [isShowingDisclaimer, setShowingDisclaimer] = useState(false)
  const timeout = useRef<NodeJS.Timeout | null>(null)

  const displayDisclaimer = () => {
    if (timeout.current) {
      clearTimeout(timeout.current)
    }
    setShowingDisclaimer(true)
    timeout.current = setTimeout(() => {
      setShowingDisclaimer(false)
    }, 4000)
  }

  return (
    <Box padding={1}>
      <Box>
        <p style={{ color: 'var(--color-link)', fontWeight: 'bold' }}>
          {formatMessage(bookingBarMessages.label.room)} {id}
        </p>
      </Box>
      <Picker
        isCompact
        text={formatMessage(bookingBarMessages.label.guests)}
        min={1}
        max={2}
        value={adults}
        onChange={onChange}
        onDisabledIncrementClick={displayDisclaimer}
      />
      <Disclaimer showDisclaimer={isShowingDisclaimer} severity="error">
        <Box marginTop={1}>
          {formatMessage(bookingBarMessages.error.tooManyAdults)}
        </Box>
      </Disclaimer>
    </Box>
  )
}
